import * as React from "react";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Notify } from "@/components/ui/notify";
import type { DisplayedIssue, IssueTarget, ValidationIssue } from "@/types/line-editor.types";

export type { ValidationIssue, IssueTarget } from "@/types/line-editor.types";

export interface ValidationPanelProps {
  issues: ValidationIssue[] | DisplayedIssue[];
  onJumpTo: (target: IssueTarget) => void;
  pendingCount?: number;
  className?: string;
}

function isDisplayed(i: ValidationIssue | DisplayedIssue): i is DisplayedIssue {
  return "state" in i;
}

export function ValidationPanel({
  issues,
  onJumpTo,
  pendingCount,
  className,
}: ValidationPanelProps): React.ReactElement {
  const derivedPending = issues.filter((i) =>
    isDisplayed(i) ? i.state === "pending" : true
  ).length;
  const count = pendingCount ?? derivedPending;
  const sorted = sortForDisplay(issues);

  return (
    <aside
      className={cn(
        "w-[260px] shrink-0 rounded-md border border-border-subtle bg-bg-surface p-3",
        "flex max-h-[calc(100vh-180px)] flex-col gap-3 overflow-auto",
        className
      )}
      aria-label="Painel de validação"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-[12px] font-semibold uppercase tracking-widest text-text-tertiary">
          Pendências
        </h3>
        <span
          className={cn(
            "rounded px-1.5 font-mono text-[11px] tabular-nums",
            count === 0
              ? "bg-rd-success-50 text-rd-success-700"
              : "bg-rd-danger-50 text-rd-danger-700"
          )}
          data-testid="validation-count"
        >
          {count}
        </span>
      </div>

      {sorted.length === 0 ? (
        <Notify.Empty
          variant="inline"
          title="Nenhuma pendência"
          description="A linha está válida e pode ser salva."
        />
      ) : (
        <ul className="space-y-1.5">
          {sorted.map((issue) => (
            <IssueItem key={issue.id} issue={issue} onJumpTo={onJumpTo} />
          ))}
        </ul>
      )}
    </aside>
  );
}

interface IssueItemProps {
  issue: ValidationIssue | DisplayedIssue;
  onJumpTo: (target: IssueTarget) => void;
}

function IssueItem({ issue, onJumpTo }: IssueItemProps) {
  const state: "pending" | "completed" = isDisplayed(issue) ? issue.state : "pending";
  const isCompleted = state === "completed";
  const tone = isCompleted ? "success" : issue.severity === "warning" ? "warning" : "danger";
  return (
    <li>
      <button
        type="button"
        onClick={() => (issue.target ? onJumpTo(issue.target) : undefined)}
        disabled={!issue.target}
        data-testid={`issue-${issue.id}`}
        data-state={state}
        className={cn(
          "flex w-full gap-2 rounded border p-2 text-left transition-colors",
          "focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]",
          tone === "danger" && "border-rd-danger-500/30 bg-rd-danger-50 hover:bg-rd-danger-100",
          tone === "warning" && "border-rd-warning-500/30 bg-rd-warning-50 hover:bg-rd-warning-100",
          tone === "success" && "border-rd-success-500/30 bg-rd-success-50 opacity-80",
          !issue.target && "cursor-default opacity-90"
        )}
        aria-label={isCompleted ? `Concluída: ${issue.message}` : issue.message}
      >
        {isCompleted ? (
          <CheckCircle2
            className="mt-0.5 h-4 w-4 shrink-0 text-rd-success-600"
            aria-hidden="true"
          />
        ) : (
          <AlertCircle
            className={cn(
              "mt-0.5 h-4 w-4 shrink-0",
              issue.severity === "danger" ? "text-rd-danger-600" : "text-rd-warning-600"
            )}
            aria-hidden="true"
          />
        )}
        <div className="min-w-0">
          <div
            className={cn(
              "text-[12px] font-medium",
              isCompleted ? "text-rd-success-700 line-through decoration-1" : "text-text-primary"
            )}
          >
            {issue.message}
          </div>
          {issue.detail && !isCompleted ? (
            <div className="mt-0.5 text-[11px] text-text-secondary">{issue.detail}</div>
          ) : null}
        </div>
      </button>
    </li>
  );
}

function sortForDisplay(issues: ValidationIssue[] | DisplayedIssue[]) {
  const list = [...issues] as Array<ValidationIssue | DisplayedIssue>;
  return list.sort((a, b) => {
    const aPending = !isDisplayed(a) || a.state === "pending";
    const bPending = !isDisplayed(b) || b.state === "pending";
    if (aPending && !bPending) return -1;
    if (!aPending && bPending) return 1;
    if (aPending && bPending) {
      const sevA = a.severity === "danger" ? 0 : 1;
      const sevB = b.severity === "danger" ? 0 : 1;
      if (sevA !== sevB) return sevA - sevB;
    }
    if (isDisplayed(a) && isDisplayed(b)) return a.order - b.order;
    return 0;
  });
}
